'use client';
import { useEffect } from 'react';
import { Tooltip } from 'react-tooltip';

import DatePickerError from '../DatePicker/Error';
import Summary from '../DatePicker/Summary';
import HelpIcon from '../Icons/Help';
import UsersIcon from '../Icons/Users';
import Button from '../Button';

import useVillageContext from '@/hooks/useVillageContext';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { Village, VillageFutureBooking } from '@/services/types';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { useCurrency } from '@/contexts/currencyContext';
import DatePickerSucessMessage from '../DatePicker/SuccessMessage';
import useVillageDates from '@/hooks/useVillageDates';
import translateFromArray from '@/utils/get-translated-object';
import useDatePickerContext from '@/hooks/useDatePickerContext';
import useVillage from '@/hooks/useVillage';
import { formatLocalizedNumber } from '@/utils/village';
import BookingCalendar from '../DatePicker/BookingCalendar';
import NotifyVillageAvailabilityModal from '../NotifyVillageAvailabilityModal';

type Dictionaries = {
  datePickerDict: IDatePickerDictonary;
  villageDict: IVillageDictionary;
  enquiryDict: IEnquiryFormDictionary;
  countriesDict: ICountriesDictionary;
  modalsDict: IModalsDictionary;
};

type Props = {
  village: Village;
  dicts: Dictionaries;
  lang: string;
  futureBookings: VillageFutureBooking[];
};

export default function AvailabilityPricePreview({
  village,
  dicts,
  lang,
  futureBookings,
}: Props) {
  const currentDate = new Date();

  const {
    totalBedrooms,
    totalGuests,
    visibleDeal,
    selectedDates,
    handleSelectDates,
    handleSelectPeriod,
    handleSelectPriceVariant,
    setCalendarPromotionTitle,
  } = useVillageContext();

  const { setCalendarErrorTypes, clearCalendarFocus } = useDatePickerContext();

  const { getSecurityDeposit } = useVillage({});
  const { getPriceVariantAndPeriod } = useVillageDates();
  const { currency, applyCurrency } = useCurrency();

  const { datePickerDict, villageDict } = dicts;
  const { availability } = villageDict;

  const currentYearPriceVariants = village.priceVariants.filter((variant) => {
    return variant.villaPricing.year === currentDate.getFullYear();
  });

  const securityDeposit = getSecurityDeposit(
    village.priceVariants,
    totalBedrooms,
    totalGuests,
  );

  const formattedSecurityDeposit = formatLocalizedNumber(
    applyCurrency(securityDeposit),
    lang,
  );

  function handleClearDates() {
    clearCalendarFocus(selectedDates);

    setCalendarErrorTypes([]);
    setCalendarPromotionTitle(undefined);
    handleSelectDates({ start: null, end: null });
  }

  useEffect(() => {
    const result = getPriceVariantAndPeriod({
      priceVariants: village.priceVariants,
      selectedDates,
      totalBedrooms,
      totalGuests,
    });

    if (result) {
      const { pricePeriod, priceVariant } = result;

      if (pricePeriod) {
        handleSelectPeriod(pricePeriod);
        handleSelectPriceVariant(priceVariant);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates]);

  useEffect(() => {
    if (visibleDeal) {
      const dealTranslation = translateFromArray({
        lang,
        translations: visibleDeal.deal.translation,
      });
      setCalendarPromotionTitle(dealTranslation?.descriptionCalendar);
    } else {
      setCalendarPromotionTitle(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleDeal]);

  return (
    <div className="m-0">
      {currentYearPriceVariants.length > 1 && (
        <div className="flex flex-wrap items-end">
          <div className="w-[calc(100%-1.25rem)] desktop:w-[calc(66.66667%-.5rem)] mx-2 mt-2 desktop:ml-8 desktop:mr-4 flex-[0_0_auto]">
            <div className="flex text-gray-700">
              <i className="mr-1">
                <UsersIcon className=" fill-current text-gray-700 w-6 h-6" />
              </i>
              <label className="relative block text-sm leading-[1.8]">
                {availability.availableUnderOccupancy}
              </label>
            </div>
          </div>
        </div>
      )}
      <div className="w-full max-w-full md:max-w-[37.5rem] desktop:max-w-[66.875rem] mt-0">
        <div className="flex flex-col md:flex-row">
          <div className="flex flex-col flex-[1_0_calc(100%-200px)] desktop:flex-[1_0_calc(100%-275px)]">
            <div className="w-full min-h-[55px]">
              <DatePickerError
                dict={datePickerDict}
                village={village}
                selectedDates={selectedDates}
              />
              <DatePickerSucessMessage />
            </div>

            <div className="relative bg-white [@media(min-width:1581px)]:h-[400px]">
              <BookingCalendar
                atPricePreview
                customFutureBookings={futureBookings}
                village={village}
                shouldFetchUnavailableDates
                confirmedDates={selectedDates}
                rangeValue={selectedDates}
                onRangeValueChange={(dates) => {
                  handleSelectDates(dates);
                }}
                extraDicts={dicts}
                lang={lang}
              />
              <Button
                disabled={
                  selectedDates.start == null && selectedDates.end == null
                }
                onClick={handleClearDates}
                className="ml-auto md:w-[12rem] hover:bg-white hover:text-[#977a0e] hover:text border-white cursor-pointer bg-white text-primary [@media(min-width:1581px)]:absolute [@media(min-width:1581px)]:right-0 [@media(min-width:1581px)]:bottom-0">
                {dicts.datePickerDict.clear}
                <span className="pl-2">{dicts.datePickerDict.dates}</span>
              </Button>
            </div>
          </div>
          <Summary
            showCalendarLegend
            dict={datePickerDict}
            className="!px-0 my-0  text-left border-t-0 md:my-0 md:pt-[35px] !pb-0 md:!px-[30px] md:flex-[1_0_200px] md:mr-[5px] desktop:flex-[1_0_270px]"
            legendClassName="flex-col"
          />
        </div>
        <div className="mt-[7rem] mb-8 pb-8 border-b-2 border-gray-200">
          {securityDeposit > 0 && (
            <span className="text-sm flex gap-1">
              <Tooltip
                clickable
                style={{
                  zIndex: 100,
                  fontSize: '14px',
                  textAlign: 'center',
                  maxWidth: '300px',
                }}
                id="deposit-tooltip">
                {availability.refoundDescription}
              </Tooltip>
              <HelpIcon
                id="deposit-tooltip"
                className="w-5 h-5 fill-current text-gray-300 cursor-pointer"
              />
              {availability.refoundLabel} {currency.symbol}{' '}
              {formattedSecurityDeposit}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
